import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Lock, UserPlus } from 'lucide-react';
import Layout from '../../components/layout/layout';
import CourseLayout from '../../components/parcours/CourseLayout';
import AuthModal from '../../components/auth/auth-modal';
import { fetchParcoursStep } from '../../lib/parcours';
import { captureInviteFromUrl } from '../../lib/parcoursInvite';
import { useAuth } from '../../AuthProvider';
import { logCandidateActivity } from '../../lib/candidateActivity';
import type { Parcours, ParcoursStep } from '../../types/parcours';

export default function ParcoursEtapePage() {
  const { companySlug, stepId } = useParams<{ companySlug: string; stepId: string }>();
  const { user, loading: authLoading } = useAuth();

  const [data, setData] = useState<{ parcours: Parcours; step: ParcoursStep } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Capture le token d'invitation ATS (?invite=) pour l'étape finale.
  useEffect(() => {
    if (companySlug) captureInviteFromUrl(companySlug);
  }, [companySlug]);

  useEffect(() => {
    if (authLoading) return;

    if (!companySlug || !stepId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    // Don't attempt to fetch steps if not authenticated — RLS will block them
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    fetchParcoursStep(companySlug, stepId)
      .then((result) => {
        if (!cancelled) {
          if (!result) setNotFound(true);
          else setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [companySlug, stepId, user, authLoading]);

  // ── Auth loading ─────────────────────────────────────────────────────────────
  if (authLoading || loading) {
    return (
      <Layout>
        <Helmet>
          <title>Chargement... | recruteurs.io</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="container-custom max-w-6xl py-24">
          <div className="animate-pulse space-y-6">
            <div className="h-6 bg-neutral-100 rounded w-1/4" />
            <div className="h-8 bg-neutral-100 rounded w-1/2" />
            <div className="h-4 bg-neutral-100 rounded w-full" />
            <div className="h-4 bg-neutral-100 rounded w-2/3" />
          </div>
        </div>
      </Layout>
    );
  }

  // ── Non connecté : écran de verrouillage ─────────────────────────────────────
  if (!user) {
    return (
      <Layout>
        <Helmet>
          <title>Connexion requise — Parcours de découverte | recruteurs.io</title>
          <meta name="robots" content="noindex" />
        </Helmet>

        <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />

        <main className="min-h-screen bg-[#f7f7f8]">
          {/* Breadcrumb */}
          <div className="bg-white border-b border-neutral-100">
            <div className="container-custom max-w-6xl py-4">
              {companySlug && (
                <Link
                  to={`/decouvrir/${companySlug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour au parcours
                </Link>
              )}
            </div>
          </div>

          <div className="container-custom max-w-2xl py-24 text-center">
            {/* Icon */}
            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#d03484] to-[#d47254] shadow-lg shadow-[#d03484]/20">
              <Lock className="h-9 w-9 text-white" />
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-neutral-950 mb-4">
              Connectez-vous pour accéder au parcours
            </h1>
            <p className="text-neutral-600 leading-relaxed mb-10 max-w-md mx-auto">
              Ce parcours est réservé aux candidats inscrits afin de protéger les informations confidentielles de l'entreprise.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Se connecter pour démarrer le parcours
              </button>
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-6 py-3.5 text-sm font-semibold text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-all"
              >
                <UserPlus className="w-4 h-4" />
                Créer un compte candidat
              </button>
            </div>

            {companySlug && (
              <Link
                to={`/decouvrir/${companySlug}`}
                className="mt-8 inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Retour à la présentation de l'entreprise
              </Link>
            )}
          </div>
        </main>
      </Layout>
    );
  }

  // ── 404 ──────────────────────────────────────────────────────────────────────
  if (notFound || !data) {
    return (
      <Layout>
        <Helmet>
          <title>Étape introuvable | recruteurs.io</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="container-custom max-w-5xl py-24 text-center">
          <p className="text-5xl font-bold text-neutral-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Étape introuvable</h1>
          <p className="text-neutral-500 mb-8">Cette étape n'existe pas ou n'est plus disponible.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {companySlug && (
              <Link
                to={`/decouvrir/${companySlug}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#d03484] hover:text-[#d47254] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour au parcours
              </Link>
            )}
            <Link
              to="/decouvrir"
              className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              Voir tous les parcours
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Utilisateur connecté : contenu complet ───────────────────────────────────
  const { parcours, step } = data;

  // Fire-and-forget activity log — never blocks rendering
  logCandidateActivity({ eventType: 'view_parcours_step', parcours, step });
  const helmetTitle = `${step.title} — ${parcours.company_name} | recruteurs.io`;
  const helmetDesc = step.description ?? parcours.description ?? parcours.tagline ?? '';

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        {helmetDesc && <meta name="description" content={helmetDesc} />}
        <link rel="canonical" href={`https://recruteurs.io/decouvrir/${companySlug}/`} />
        <meta name="robots" content="noindex, follow" />
        {parcours.og_image && <meta property="og:image" content={parcours.og_image} />}
      </Helmet>

      <CourseLayout parcours={parcours} currentStep={step} />
    </>
  );
}
